import { useState, type FormEvent } from "react";
import { AuthLayout } from "../components/AuthLayout";
import { Select, TextField } from "../components/ui";
import { AuthSubmit } from "../components/AuthSubmit";
import { PasswordFields } from "../components/PasswordFields";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { humanizeAuthError } from "../authErrors";
import { isValidEmail } from "../validation";
import { PASSWORD_MIN, passwordsReady } from "../password";
import { useI18n, type TranslationKey } from "../i18n";
import { useAuthNav } from "../authTransition";
import { useFormError } from "../useFormError";
import "./auth.css";

const ORG_SIZE_OPTIONS = [
  "1–10",
  "11–50",
  "51–200",
  "201–500",
  "501–1000",
  "1000+",
];

const INDUSTRY_OPTIONS: { value: string; labelKey: TranslationKey }[] = [
  { value: "Technology", labelKey: "auth.industryTechnology" },
  { value: "Finance", labelKey: "auth.industryFinance" },
  { value: "Healthcare", labelKey: "auth.industryHealthcare" },
  { value: "Manufacturing", labelKey: "auth.industryManufacturing" },
  { value: "Retail", labelKey: "auth.industryRetail" },
  { value: "Government", labelKey: "auth.industryGovernment" },
  { value: "Education", labelKey: "auth.industryEducation" },
  { value: "Energy", labelKey: "auth.industryEnergy" },
  { value: "Media", labelKey: "auth.industryMedia" },
  { value: "Other", labelKey: "auth.industryOther" },
];

const PROFILE_DRAFT_KEY = "ces.profile.draft";

export default function RegistrationPage() {
  const { go } = useAuthNav();
  const { pendingEmail, setPendingEmail, signUp } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState(pendingEmail ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [orgSize, setOrgSize] = useState("");
  const [industry, setIndustry] = useState("");
  const [busy, setBusy] = useState(false);
  const { error, setError, formRef } = useFormError();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setError(t("auth.invalidEmail"), 'input[type="email"]');
      return;
    }
    if (password.length < PASSWORD_MIN) {
      setError(t("auth.passwordWeak"), ".pwf");
      return;
    }
    if (confirm !== password) {
      setError(t("auth.passwordsMismatch"), ".pwf");
      return;
    }
    const display_name =
      [firstName, lastName].map((s) => s.trim()).filter(Boolean).join(" ") ||
      email.split("@")[0];
    setBusy(true);
    setError(null);
    try {
      await signUp({ email, password, display_name });
      try {
        localStorage.setItem(
          PROFILE_DRAFT_KEY,
          JSON.stringify({
            organisation: organisation.trim(),
            organisation_size: orgSize,
            industry,
          }),
        );
      } catch {
        // storage quota / disabled — non-fatal
      }
      setPendingEmail(email);
      try {
        await api.startEmailVerification();
      } catch (err) {
        console.warn("startEmailVerification failed:", err);
      }
      go("/verify");
    } catch (err) {
      setError(humanizeAuthError(err, t, "auth.signupFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("auth.registrationTitle")} showBack>
      <form ref={formRef} className="auth__form anim-stagger" onSubmit={onSubmit} noValidate>
        <TextField
          type="email"
          name="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("auth.emailPlaceholder")}
        />
        <PasswordFields
          password={password}
          confirm={confirm}
          onPasswordChange={setPassword}
          onConfirmChange={setConfirm}
          placeholder={t("auth.createPassword")}
          focused
          autoFocus
          disabled={busy}
        />
        <div className="divider" />
        <TextField
          name="given-name"
          autoComplete="given-name"
          placeholder={t("auth.name")}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextField
          name="family-name"
          autoComplete="family-name"
          placeholder={t("auth.surname")}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <TextField
          name="organization"
          autoComplete="organization"
          placeholder={t("auth.organisation")}
          value={organisation}
          onChange={(e) => setOrganisation(e.target.value)}
        />
        <Select
          placeholder={t("auth.organisationSize")}
          value={orgSize}
          onChange={(e) => setOrgSize(e.target.value)}
        >
          {ORG_SIZE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Select>
        <Select
          placeholder={t("auth.industry")}
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
        >
          {INDUSTRY_OPTIONS.map(({ value, labelKey }) => (
            <option key={value} value={value}>
              {t(labelKey)}
            </option>
          ))}
        </Select>
        {error ? <p className="auth-error">{error}</p> : null}
        <AuthSubmit busy={busy} ready={passwordsReady(password, confirm)}>
          {busy ? t("auth.creating") : t("auth.signUp")}
        </AuthSubmit>
      </form>
    </AuthLayout>
  );
}
